import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, QueryStats, Terminal } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import * as apiLogs from '@/api/apiLogs';
import { toApiError } from '@/api/client';
import { ApiProductRouteGuard } from '@/components/ApiProductRouteGuard';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/api-logs/')({
  component: ApiLogsPage,
});

function statusColor(code: number): 'success' | 'warning' | 'error' | 'default' {
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  if (code >= 200 && code < 300) return 'success';
  return 'default';
}

function ApiLogsPage(): JSX.Element {
  return (
    <ApiProductRouteGuard>
      <ApiLogsContent />
    </ApiProductRouteGuard>
  );
}

function ApiLogsContent(): JSX.Element {
  const [tab, setTab] = useState<'requests' | 'consumption'>('requests');

  return (
    <Box>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Terminal color="primary" /> Logs de API
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Solicitudes a la API con correlation ID y consumo por endpoint (campos sensibles enmascarados).
        </Typography>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Solicitudes" value="requests" icon={<Terminal fontSize="small" />} iconPosition="start" />
        <Tab label="Consumo" value="consumption" icon={<QueryStats fontSize="small" />} iconPosition="start" />
      </Tabs>

      {tab === 'requests' ? <Requests /> : <Consumption />}
    </Box>
  );
}

function Requests(): JSX.Element {
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [endpoint, setEndpoint] = useState('');

  const filter: apiLogs.ApiLogFilter = {
    method: method || undefined,
    status: status ? Number(status) : undefined,
    endpoint: endpoint || undefined,
  };

  const logsQ = useQuery({
    queryKey: ['api-logs', method, status, endpoint],
    queryFn: () => apiLogs.listApiLogs(filter, 0, 100),
  });

  const items = logsQ.data?.items ?? [];

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" gap={1.5}>
        <TextField select size="small" label="Método" value={method} onChange={(e) => setMethod(e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="">Todos</MenuItem>
          {['GET', 'POST', 'PUT', 'DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="200">2xx (200)</MenuItem>
          <MenuItem value="400">4xx (400)</MenuItem>
          <MenuItem value="401">401</MenuItem>
          <MenuItem value="500">5xx (500)</MenuItem>
        </TextField>
        <TextField size="small" label="Endpoint contiene" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} sx={{ minWidth: 220 }} />
      </Stack>

      {logsQ.isLoading ? (
        <LoadingScreen message="Cargando solicitudes…" />
      ) : logsQ.error ? (
        <MuiAlert severity="error">{toApiError(logsQ.error).message}</MuiAlert>
      ) : items.length === 0 ? (
        <EmptyState icon={<Terminal fontSize="inherit" />} title="Sin solicitudes" description="No hay registros para este filtro." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Método</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell align="right">Estado</TableCell>
                <TableCell align="right">Latencia</TableCell>
                <TableCell>Correlation ID</TableCell>
                <TableCell>Client / IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDateTime(l.occurredAt)}</Typography></TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={l.method} /></TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>{l.endpoint}</Typography></TableCell>
                  <TableCell align="right"><Chip size="small" color={statusColor(l.statusCode)} variant="outlined" label={l.statusCode} /></TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{l.latencyMs} ms</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>{l.correlationId.slice(0, 8)}…</Typography>
                      <Tooltip title="Copiar correlation ID">
                        <IconButton size="small" onClick={() => void navigator.clipboard?.writeText(l.correlationId)}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {l.clientId ?? '—'}{l.ip ? ` · ${l.ip}` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function Consumption(): JSX.Element {
  const consQ = useQuery({
    queryKey: ['api-consumption'],
    queryFn: () => apiLogs.getApiConsumption(),
  });

  if (consQ.isLoading) return <LoadingScreen message="Cargando consumo…" />;
  if (consQ.error) return <MuiAlert severity="error">{toApiError(consQ.error).message}</MuiAlert>;

  const endpoints = consQ.data?.endpoints ?? [];
  const totalCalls = endpoints.reduce((n, e) => n + e.count, 0);

  if (endpoints.length === 0) {
    return <EmptyState icon={<QueryStats fontSize="inherit" />} title="Sin consumo" description="No hay actividad de API en los últimos 30 días." />;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Últimos 30 días · <strong>{totalCalls.toLocaleString('es-AR')}</strong> operaciones
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Endpoint</TableCell>
              <TableCell align="right">Llamadas</TableCell>
              <TableCell align="right">Éxito</TableCell>
              <TableCell align="right">p99</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...endpoints].sort((a, b) => b.count - a.count).map((e) => (
              <TableRow key={e.endpoint} hover>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>{e.endpoint}</Typography></TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{e.count.toLocaleString('es-AR')}</TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={e.successRate >= 0.99 ? 'success' : e.successRate >= 0.9 ? 'warning' : 'error'}
                    label={`${Math.round(e.successRate * 100)}%`}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{e.p99LatencyMs} ms</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
